import React, { useEffect, useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import { getVideoStreamingUrl } from '../../../utilities/temporaryUrlUtils';
import { isEnvCdnUrl } from '../../../utilities/cdnUtils';

// Register the qualityLevels plugin only once globally
let qualityLevelsPluginRegistered = false;
if (!qualityLevelsPluginRegistered) {
  try {
    require('videojs-contrib-quality-levels');
    qualityLevelsPluginRegistered = true;
  } catch (error) {
    console.warn('[VideoRenderer] Failed to load quality levels plugin:', error);
  }
}

interface VideoRendererProps {
  url?: string;
  file?: File;
  filename?: string;
  mode?: 'preview' | 'thumbnail' | 'dialog';
  width?: string | number;
  height?: string | number;
  onLoad?: () => void;
  onError?: () => void;
  onClick?: () => void;
}


// Helper function to enforce high quality levels only (Starlink fix)
const enforceHighQualityLevels = (player: any) => {
  if (!player || player.isDisposed() || !player.qualityLevels) {
    return;
  }
  
  const qualityLevels = player.qualityLevels();
  if (!qualityLevels || qualityLevels.length <= 2) {
    return; // Not enough levels to enforce
  }
  
  console.log('[VideoRenderer] 🎯 STARLINK FIX: Enforcing high quality levels only');
  
  // Enable only levels 0 and 1 (highest quality levels)
  qualityLevels[0].enabled = true; // Highest quality
  if (qualityLevels.length > 1) {
    qualityLevels[1].enabled = true; // Second highest quality
  }
  
  // Disable all lower quality levels (2, 3, 4, etc.)
  for (let i = 2; i < qualityLevels.length; i++) {
    qualityLevels[i].enabled = false;
    console.log(`[VideoRenderer] 🚫 Disabled Level ${i} (${qualityLevels[i].width}x${qualityLevels[i].height})`);
  }
  
  console.log('[VideoRenderer] ✅ Quality lock active: Only levels 0 and 1 available');
};

// Setup quality selector for HLS streams
const setupQualitySelector = (player: any) => {
  console.log('[VideoRenderer] Setting up standard quality selector...');
  
  // Guard against disposed or invalid players
  if (!player || player.isDisposed() || !player.qualityLevels) {
    console.log('[VideoRenderer] Player is disposed or invalid, skipping quality selector setup');
    return;
  }
  
  // Guard to prevent multiple quality selectors
  let qualitySelectorCreated = false;
  
  const createQualityMenu = () => {
    if (qualitySelectorCreated) {
      console.log('[VideoRenderer] Quality selector already created, skipping');
      return;
    }
    
    // Guard against disposed or invalid players
    if (!player || player.isDisposed() || !player.qualityLevels) {
      console.log('[VideoRenderer] Player is disposed or invalid, skipping quality menu creation');
      return;
    }
    
    // Get the official quality levels list
    const qualityLevels = player.qualityLevels();
    
    if (!qualityLevels || qualityLevels.length <= 1) {
      console.log('[VideoRenderer] Not enough quality levels available:', qualityLevels?.length || 0);
      return;
    }
    
    console.log('[VideoRenderer] Found', qualityLevels.length, 'quality levels');
    
    // Use standard Video.js components
    const MenuButton = (videojs as any).getComponent('MenuButton');
    const MenuItem = (videojs as any).getComponent('MenuItem');
    
    // Standard MenuItem for quality selection
    class QualityMenuItem extends MenuItem {
      qualityIndex: number;
      
      constructor(player: any, options: any) {
        super(player, options);
        this.qualityIndex = options.qualityIndex;
        
        if (options.selected) {
          this.selected(true);
        }
      }
      
      handleClick() {
        console.log('[VideoRenderer] Quality item clicked:', this.qualityIndex);
        
        // Guard against disposed or invalid players
        if (!this.player() || this.player().isDisposed() || !this.player().qualityLevels) {
          console.log('[VideoRenderer] Player is disposed or invalid, skipping quality switch');
          return;
        }
        
        const qualityLevels = this.player().qualityLevels();
        if (!qualityLevels) {
          console.log('[VideoRenderer] Quality levels not available, skipping quality switch');
          return;
        }
        
        const menuButton = this.player().getChild('controlBar').getChild('QualityMenuButton');
        
        // CRITICAL: Store current playback position and state before switching
        const currentTime = this.player().currentTime();
        const isPaused = this.player().paused();
        const wasPlaying = !isPaused;
        
        console.log('[VideoRenderer] Quality switch - preserving position:', {
          currentTime,
          isPaused,
          wasPlaying,
          qualityIndex: this.qualityIndex
        });
        
        // Update selection state
        if (menuButton && menuButton.menu) {
          const menuItems = menuButton.menu.children();
          menuItems.forEach((item: any) => {
            if (item.selected) {
              item.selected(false);
            }
          });
        }
        
        // Set this item as selected
        this.selected(true);
        
        // Apply quality selection with sync preservation
        if (this.qualityIndex === -1) {
          // Auto - enable all levels for adaptive streaming
          for (let i = 0; i < qualityLevels.length; i++) {
            qualityLevels[i].enabled = true;
          }
          console.log('[VideoRenderer] Selected quality: Auto (adaptive)');
        } else {
          // Disable all levels first
          for (let i = 0; i < qualityLevels.length; i++) {
            qualityLevels[i].enabled = false;
          }
          // Enable selected level
          qualityLevels[this.qualityIndex].enabled = true;
          const selectedLevel = qualityLevels[this.qualityIndex];
          console.log('[VideoRenderer] Selected quality:', selectedLevel.height + 'p');
        }
        
        // CRITICAL: Restore playback position and state after quality switch
        const restorePlaybackState = () => {
          if (currentTime > 0) {
            this.player().currentTime(currentTime);
            console.log('[VideoRenderer] Restored playback position to:', currentTime);
          }
          
          if (wasPlaying) {
            // Small delay to ensure the new quality level is loaded
            setTimeout(() => {
              this.player().play().catch((error: any) => {
                console.warn('[VideoRenderer] Failed to resume playback after quality switch:', error);
              });
            }, 100);
          }
        };
        
        // Wait for the quality switch to take effect
        if (currentTime > 0) {
          // Listen for loadedmetadata event to know when new quality is ready
          const onMetadataLoaded = () => {
            this.player().off('loadedmetadata', onMetadataLoaded);
            restorePlaybackState();
          };
          
          this.player().on('loadedmetadata', onMetadataLoaded);
          
          // Fallback timeout in case loadedmetadata doesn't fire
          setTimeout(() => {
            this.player().off('loadedmetadata', onMetadataLoaded);
            restorePlaybackState();
          }, 1000);
        }
        
        // Update button text
        this.updateButtonText();
      }
      
      updateButtonText() {
        // Guard against disposed or invalid players
        if (!this.player() || this.player().isDisposed()) {
          console.log('[VideoRenderer] Player is disposed or invalid, skipping button text update');
          return;
        }
        
        const menuButton = this.player().getChild('controlBar').getChild('QualityMenuButton');
        if (menuButton && menuButton.el()) {
          const buttonText = menuButton.el().querySelector('.vjs-icon-placeholder');
          if (buttonText) {
            let newText = 'AUTO';
            if (this.qualityIndex !== -1) {
              // Guard against disposed or invalid players
              if (!this.player().qualityLevels) {
                console.log('[VideoRenderer] Quality levels not available, using AUTO text');
              } else {
                const qualityLevels = this.player().qualityLevels();
                if (qualityLevels && qualityLevels[this.qualityIndex]) {
                  const selectedLevel = qualityLevels[this.qualityIndex];
                  newText = selectedLevel.height + 'p';
                }
              }
            }
            buttonText.textContent = newText;
          }
        }
      }
    }
    
    // Standard MenuButton for quality selection
    class QualityMenuButton extends MenuButton {
      constructor(player: any, options: any) {
        super(player, options);
        this.controlText('Quality');
        this.addClass('vjs-quality-selector');
        
        this.updateButtonText();
      }
      
      updateButtonText() {
        const buttonEl = this.el();
        if (buttonEl) {
          const iconPlaceholder = buttonEl.querySelector('.vjs-icon-placeholder');
          if (iconPlaceholder) {
            iconPlaceholder.textContent = 'AUTO';
          }
        }
      }
      
      createItems() {
        const items: any[] = [];
        
        // Guard against disposed or invalid players
        if (!this.player() || this.player().isDisposed() || !this.player().qualityLevels) {
          console.log('[VideoRenderer] Player is disposed or invalid, returning empty items');
          return items;
        }
        
        const qualityLevels = this.player().qualityLevels();
        if (!qualityLevels) {
          console.log('[VideoRenderer] Quality levels not available, returning empty items');
          return items;
        }
        
        // Add "Auto" option first (selected by default)
        items.push(new QualityMenuItem(this.player(), {
          label: 'Auto',
          qualityIndex: -1,
          selected: true
        }));
        
        // Sort quality levels by height (highest first)
        const sortedLevels: any[] = [];
        for (let i = 0; i < qualityLevels.length; i++) {
          sortedLevels.push({ index: i, level: qualityLevels[i] });
        }
        sortedLevels.sort((a, b) => (b.level.height || 0) - (a.level.height || 0));
        
        // Add quality level menu items
        sortedLevels.forEach(({ index, level }) => {
          const height = level.height || 0;
          const label = `${height}p`;
          
          items.push(new QualityMenuItem(this.player(), {
            label: label,
            qualityIndex: index,
            selected: false
          }));
        });
        
        return items;
      }
    }
    
    // Check if quality button already exists and remove it
    const controlBar = player.getChild('controlBar');
    const existingButton = controlBar.getChild('QualityMenuButton');
    if (existingButton) {
      controlBar.removeChild(existingButton);
    }
    
    // Register and add the quality button
    (videojs as any).registerComponent('QualityMenuButton', QualityMenuButton);
    controlBar.addChild('QualityMenuButton', {}, 6);
    
    console.log('[VideoRenderer] Standard quality selector created successfully');
    qualitySelectorCreated = true;
  };
  
  // Listen for when quality levels are added
  const qualityLevels = player.qualityLevels();
  
  // Try immediately if levels already exist
  if (qualityLevels && qualityLevels.length > 1) {
    createQualityMenu();
    // Enforce high quality levels only (Starlink fix)
    enforceHighQualityLevels(player);
  }
  
  // Listen for quality levels being added
  if (qualityLevels) {
    qualityLevels.on('addqualitylevel', () => {
      console.log('[VideoRenderer] Quality level added, total:', qualityLevels.length);
      if (qualityLevels.length > 1) {
        createQualityMenu();
        // Enforce high quality levels only (Starlink fix)
        enforceHighQualityLevels(player);
      }
    });
  }
  
  // Also try after a delay to catch late-loading manifests
  setTimeout(() => {
    // Double-check player is still valid before accessing qualityLevels
    if (!player || player.isDisposed() || !player.qualityLevels) {
      console.log('[VideoRenderer] Player is disposed or invalid, skipping delayed quality setup');
      return;
    }
    
    const delayedQualityLevels = player.qualityLevels();
    if (delayedQualityLevels && delayedQualityLevels.length > 1) {
      createQualityMenu();
      // Enforce high quality levels only (Starlink fix)
      enforceHighQualityLevels(player);
    }
  }, 2000);
};

// Custom CSS for quality selector
const qualitySelectorStyles = `
  .vjs-quality-selector {
    font-family: Arial, sans-serif;
  }
  
  .vjs-quality-selector .vjs-menu-content {
    background: rgba(0, 0, 0, 0.9) !important;
    border-radius: 4px !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5) !important;
    min-width: 100px !important;
  }
  
  .vjs-quality-selector .vjs-menu-item {
    color: white !important;
    padding: 8px 12px !important;
    font-size: 13px !important;
    transition: background-color 0.2s ease !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
  
  .vjs-quality-selector .vjs-menu-item:last-child {
    border-bottom: none !important;
  }
  
  .vjs-quality-selector .vjs-menu-item:hover {
    background: rgba(255, 255, 255, 0.1) !important;
    color: white !important;
  }
  
  .vjs-quality-selector .vjs-menu-item.vjs-selected {
    background: rgba(255, 255, 255, 0.2) !important;
    color: white !important;
    font-weight: bold !important;
  }
  
  .vjs-quality-selector .vjs-menu-item.vjs-selected:hover {
    background: rgba(255, 255, 255, 0.3) !important;
  }
  
  .vjs-quality-selector .vjs-icon-placeholder {
    font-size: 12px !important;
    color: white !important;
    text-align: center !important;
    padding: 0 4px !important;
  }
`;

export const VideoRenderer: React.FC<VideoRendererProps> = React.memo(({
  url,
  file,
  filename,
  mode = 'preview',
  width = '100%',
  height = '100%',
  onLoad,
  onError,
  onClick
}) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);

  // Resolve the video source (file or URL) - prioritize URL to prevent memory leaks
  const videoSource = useMemo(() => {
    // If URL is provided, use it directly - don't create additional blob URLs
    if (url) {
      return url;
    }
    // Only create blob URL if no URL was provided
    if (file) {
      return URL.createObjectURL(file);
    }
    return undefined;
  }, [url, file]);

  console.log('[VideoRenderer] Rendering with source:', { url, file: file?.name, resolved: videoSource });

  // Cleanup blob URL when component unmounts or file changes
  // Only cleanup if we created the blob URL ourselves (when no url was provided)
  useEffect(() => {
    return () => {
      if (!url && file && videoSource && videoSource.startsWith('blob:')) {
        URL.revokeObjectURL(videoSource);
      }
    };
  }, [url, file, videoSource]);

  // Handle resize events to fix quality selector positioning
  useEffect(() => {
    const handleResize = () => {
      const player = playerRef.current;
      if (player && !player.isDisposed()) {
        // Force quality selector menu to recalculate position
        const qualityButton = player.getChild('controlBar')?.getChild('QualityMenuButton');
        if (qualityButton && qualityButton.menu) {
          qualityButton.menu.hide(); // Hide menu on resize to prevent positioning issues
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Memoize video player setup to prevent recreation on resize
  const videoPlayerSetup = useMemo(() => {
    if (!videoSource) return null;
    
    return {
      source: videoSource,
      shouldSetup: true
    };
  }, [videoSource]); // Only recreate if video source changes, not on resize

  // Video.js player setup
  useEffect(() => {
    if (!videoPlayerSetup?.shouldSetup || !videoRef.current || playerRef.current) return;

    // Inject custom CSS for quality selector
    const existingStyles = document.querySelector('#vjs-quality-selector-styles');
    if (!existingStyles) {
      const styleElement = document.createElement('style');
      styleElement.id = 'vjs-quality-selector-styles';
      styleElement.innerHTML = qualitySelectorStyles;
      document.head.appendChild(styleElement);
    }

    // URLs from temporaryUrlUtils are already properly encoded with standard URL encoding
    // (%20 for spaces, %2B for literal +). No additional encoding needed here.

    // Use memoized video source
    const currentVideoSource = videoPlayerSetup.source;

    // Create video element
    const videoElement = document.createElement("video-js");
    videoElement.classList.add('vjs-big-play-centered');
    videoRef.current.appendChild(videoElement);

    // Initialize Video.js player
    const player = playerRef.current = videojs(videoElement, {
      controls: true,
      responsive: true,
      fluid: true,
      autoplay: mode === 'dialog',
      muted: false,
      sources: [{
        src: currentVideoSource,
        type: currentVideoSource.includes('.m3u8') ? 'application/x-mpegURL' : 'video/mp4'
      }],
      html5: {
        withCredentials: true,
        vhs: { 
          withCredentials: true,
          enableLowInitialPlaylist: false,
          smoothQualityChange: true,
          overrideNative: true,
          // Enhanced settings for quality switching and A/V sync
          allowSeeksWithinUnsafeLiveWindow: true,
          experimentalBufferBasedABR: true,
          experimentalLLHLS: false,
          // Optimize buffering for smooth transitions
          experimentalExactManifestTimings: true,
          // Improve sync tolerance
          liveRangeSafeTimeDelta: 30,
          // Quality switching optimization
          bandwidth: 1000000, // Start with reasonable bandwidth estimate
          fastQualityChange: true,
        }
      },
      plugins: {},
      playbackRates: [0.5, 1, 1.25, 1.5, 2],
    });

    player.ready(() => {
      console.log('[VideoRenderer] Video.js player ready');
      
      // Set up quality selector for HLS streams
      if (currentVideoSource.includes('.m3u8')) {
        setupQualitySelector(player);
        
        // Try key events to catch when HLS manifest is loaded
        player.on('loadedmetadata', () => {
          if (player && !player.isDisposed()) {
            setupQualitySelector(player);
            // Enforce high quality levels only (Starlink fix)
            enforceHighQualityLevels(player);
          }
        }); 
        
        // Also try after delay when the HLS manifest should be loaded
        setTimeout(() => {
          if (player && !player.isDisposed()) {
            setupQualitySelector(player);
            // Enforce high quality levels only (Starlink fix)
            enforceHighQualityLevels(player);
          }
        }, 2000);
      }
      
      if (onLoad) onLoad();
    });

    player.on('error', (error: any) => {
      console.error('[VideoRenderer] Video.js error:', error);
      if (onError) onError();
    });

    return () => {
      if (playerRef.current && !playerRef.current.isDisposed()) {
        try {
          playerRef.current.dispose();
        } catch (error) {
          console.warn('[VideoRenderer] Error disposing player:', error);
        }
        playerRef.current = null;
      }
    };
  }, [videoPlayerSetup, mode, onLoad, onError]);

  // Early return if no video source provided
  if (!videoPlayerSetup?.source) {
    return (
      <Box sx={{ 
        width, 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.1)',
        borderRadius: 1
      }}>
        <Typography color="text.secondary">No video source provided</Typography>
      </Box>
    );
  }

  // Thumbnail mode
  if (mode === 'thumbnail') {
    return (
      <Box 
        sx={{ 
          width, 
          height, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          cursor: onClick ? 'pointer' : 'default',
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: 1
        }}
        onClick={onClick}
      >
        <Typography>▶️ Video</Typography>
      </Box>
    );
  }

  // Full video player
  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex',
      justifyContent: 'center',
      alignItems: mode === 'dialog' ? 'center' : 'flex-start',
      overflow: 'hidden',
      '& .video-js': { 
        width: mode === 'dialog' ? '100%' : '85vw',
        height: 'auto',
        aspectRatio: '16/9',
        maxWidth: '100%',
        maxHeight: mode === 'dialog' ? '85vh' : '55vh',
        minWidth: mode === 'dialog' ? '75vw' : 'auto',
        objectFit: 'contain',
      } 
    }}>
      <div data-vjs-player style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <div ref={videoRef} style={{ width: '100%', height: '100%' }} />
      </div>
    </Box>
  );
});

export default VideoRenderer; 